import { useState } from "react";

import { ValidationError, ObjectSchema } from "yup";
import { ObjectShape } from "yup/lib/object";

import { DifferentLocation } from "../../../../../../../../models/location";
import { Warehouse } from "../../../../../../../../models/warehouse";
import { capitalizeFirstLetter } from "../../../../../../../../util/capitalizeFirstLetter";
import { Status } from "../../../../../../../../util/enums";

export enum Tabs {
  WAREHOUSE = "warehouse",
  BRANCHES = "branches",
  SECTIONS = "sections",
  CONTACTS = "contacts",
}

export const useWarehouseActions = () => {
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [options, setOptions] = useState<string[]>([Tabs.WAREHOUSE]);
  const [currentStep, setCurrentStep] = useState<string>(Tabs.WAREHOUSE);
  const [isUsingCompanyAddress, setIsUsingCompanyAddress] = useState<boolean>(false);
  const [selectLeftPanel, setSelectLeftPanel] = useState<number>(0);
  const [warehouseStepHasError, setWarehouseStepHasError] = useState<boolean>(false);
  const [branchStepHasError, setBranchStepHasError] = useState<boolean>(false);
  const [sectionStepHasError, setSectionStepHasError] = useState<boolean>(false);

  const [companyLocation, setCompanyLocation] = useState<DifferentLocation>();

  const getPathsFromError = (errors: ValidationError[]): string => {
    const paths: string[] = [];

    for (const err of errors) {
      if (err.path) {
        if (err.path?.includes("[0]")) {
          const path = err.path.split("[0]")[0];
          const pathCap = capitalizeFirstLetter(path);

          if (!paths.includes(pathCap)) paths.push(pathCap);
        } else {
          const pathCap = capitalizeFirstLetter(err.path);

          if (!paths.includes(pathCap)) paths.push(pathCap);
        }
      }
    }

    return paths.join(", ");
  };

  const handleUpdateWarehouse = (warehouse?: Warehouse) => {
    let warehouseToUpdate = warehouse;

    if (warehouseToUpdate && !warehouseToUpdate.status) {
      warehouseToUpdate = { ...warehouse, status: Status.INACTIVE };
    }
    setWarehouse((state) => (state ? { ...state, ...warehouseToUpdate } : warehouseToUpdate));
  };

  const handleClearFields = () => setWarehouse({ status: Status.INACTIVE });

  const handleValidateSchemaSuccess = (schema: ObjectSchema<ObjectShape>): boolean => {
    schema.validateSync(
      {
        code: warehouse?.code,
        name: warehouse?.name,
        location: warehouse?.location,
        hasBranch: currentStep === Tabs.BRANCHES || !!warehouse?.id,
        hasSection:
          currentStep === Tabs.SECTIONS || currentStep === Tabs.CONTACTS || !!warehouse?.id,
        branches: warehouse?.branches,
        sections: warehouse?.sections?.map((section) => ({
          ...section,
          containsField: section.allowsItemLoose || section.allowsStorageBox,
          isItemLooseOnly: section.allowsItemLoose && !section.allowsStorageBox,
        })),
      },
      { abortEarly: false }
    );
    setErrors([]);

    if (currentStep === Tabs.WAREHOUSE) setWarehouseStepHasError(false);
    if (currentStep === Tabs.BRANCHES) setBranchStepHasError(false);
    if (currentStep === Tabs.SECTIONS) setSectionStepHasError(false);

    return true;
  };

  const handleValidateSchemaError = (error: ValidationError): boolean => {
    if (error) {
      setErrors(error.inner);
      const paths = getPathsFromError(error?.inner).toLowerCase();

      if (
        paths.includes("code") ||
        (!paths.includes("sections") && paths.includes("name")) ||
        paths.includes("longAddress") ||
        paths.includes("location")
      ) {
        setSelectLeftPanel(-1);
      } else if (paths.includes("branches")) {
        setSelectLeftPanel(1);
      }
    }

    if (currentStep === Tabs.WAREHOUSE) setWarehouseStepHasError(true);
    if (currentStep === Tabs.BRANCHES) setBranchStepHasError(true);
    if (currentStep === Tabs.SECTIONS) setSectionStepHasError(true);

    return false;
  };

  const handleValidate = (schema: ObjectSchema<ObjectShape>): boolean => {
    try {
      const validateSuccess = handleValidateSchemaSuccess(schema);

      return validateSuccess;
    } catch (error) {
      if (error instanceof ValidationError) {
        return handleValidateSchemaError(error);
      }

      return false;
    }
  };

  const nextStep = (step: string) => {
    if (!options.includes(step)) setOptions((state) => (state ? [...state, step] : [step]));
    setCurrentStep(step);
  };

  const previousStep = (nextOptions: string[], nextStep: string) => {
    setOptions([...nextOptions]);
    setCurrentStep(nextStep);
  };

  const handleSetLocation = (isUsingCompanyLocation: boolean, location: DifferentLocation) => {
    if (isUsingCompanyLocation) {
      return {
        location: companyLocation ?? location,
        locationId:
          companyLocation && companyLocation.id ? Number(companyLocation.id) : Number(location.id),
      };
    }

    return { location: undefined, locationId: undefined };
  };

  const handleUpdateWarehouseLocation = (
    isUsingCompanyLocation: boolean,
    location: DifferentLocation
  ) => {
    const locationToUpdate = handleSetLocation(isUsingCompanyLocation, location);

    if (warehouse) {
      handleUpdateWarehouse({ ...warehouse, ...locationToUpdate });
    } else {
      handleUpdateWarehouse({ ...locationToUpdate });
    }
  };

  return {
    warehouse,
    options,
    currentStep,
    errors,
    isUsingCompanyAddress,
    companyLocation,
    selectLeftPanel,
    setSelectLeftPanel,
    setCompanyLocation,
    setIsUsingCompanyAddress,
    handleUpdateWarehouseLocation,
    handleUpdateWarehouse,
    handleClearFields,
    handleValidate,
    nextStep,
    previousStep,
    sectionStepHasError,
    warehouseStepHasError,
    branchStepHasError,
  };
};
